import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Heading, Text, TextButton } from '@zillow/constellation-10';

import { useGetListingDetails, useGetListingRecommendations } from '../../../../../hooks/queries/properties';
import propertyUtils from '../../../../../utils/propertyUtils';
import stringHelper from '../../../../../utils/stringHelper';
import { getIsInputFormDisabled } from '../../../../MFAccordion/utils';

import { REVIEW_PAGE_SECTIONS } from '../../../../../constants/ListingDetailConstants';
import { RECS } from '../../../../../constants/RecommendationsConstants';
import { SF_MODAL_COMPONENTS } from '../constants';

import AccordionIconError from '../../../../../../srcLegacy/shared/components/WizardModularListing/Review/components/AccordionIconError';
import FieldValueDisplay from '../../../../../../srcLegacy/shared/components/WizardModularListing/Review/components/FieldValueDisplay';
import RecommendationModal from '../../../../modals/RecommendationModal';
import ContentSectionEditButton from '../components/ContentSectionEditButton';

import { iconPlaceholderStyles, reviewSectionStyles, reviewSectionTitleStyles, reviewTitleRowStyles } from '../styles';

import { useAuth } from '../../../../AuthProvider';
import RecommendationIconBulb from '../../../../PostingPath/RecommendationIconBulb';

import type { MouseEvent } from 'react';
import type { GetUserPropertyDetailsAmenitiesGroupWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

const ACTION_NEEDED = 'Action needed';

const ESCAPE_VALUES = {
  'Ev ': 'EV ',
  ' ev ': ' EV ',
} as const;

const getDisplayValue = ({
  amenitiesToCompute,
}: {
  amenitiesToCompute: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>;
}) => {
  return amenitiesToCompute.reduce((acc: Array<JSX.Element>, { category, values, isRequired }) => {
    const displayValue = values
      .reduce((a: Array<string>, { displayName, isSelected }) => (!isSelected ? a : (a.push(displayName), a)), [])
      .join(', ');

    if (displayValue || isRequired) {
      let finalDisplayValue = displayValue ? stringHelper.capitalize(displayValue.toLowerCase()) : '';

      if (finalDisplayValue) {
        Object.entries(ESCAPE_VALUES).forEach(([key, value]) => {
          finalDisplayValue = finalDisplayValue.replace(key, value);
        });
      }

      acc.push(
        <div key={category}>
          {category}: {finalDisplayValue || (isRequired && <Text color="text.subtle">{ACTION_NEEDED}</Text>)}
        </div>,
      );
    }

    return acc;
  }, []);
};

type Props = {
  onEditClick: (e: MouseEvent<HTMLButtonElement>) => void;
};

const AmenitiesContent = ({ onEditClick }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData, recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });

  const [displayRecsModal, setDisplayRecsModal] = useState('');
  const recommendationTypes = new Set(recommendationsData?.recommendations.map((r) => r.type) ?? []);

  const isFeed = listingDetails?.isFeed || false;
  const isDisabled = getIsInputFormDisabled(listingDetails, user?.features?.canUpdateListing ?? false);
  const active = listingDetails?.active || false;

  const interior = listingDetails?.amenities?.interior ?? [];
  const property = listingDetails?.amenities?.property ?? [];
  const propertyAmenities = listingDetails?.propertyAmenities ?? [];

  const customNoneFieldValueText = isFeed ? 'None' : ACTION_NEEDED;

  const isInteriorRequirementFulfilled = propertyUtils.checkIsAmenityFieldFulfilled({ amenityField: interior });

  const displayInteriorAmenities = getDisplayValue({ amenitiesToCompute: interior });
  const displayPropertyAmenities = getDisplayValue({ amenitiesToCompute: property });

  const displayAdditionalAmenities = propertyAmenities.map(({ id, amenity }: { id: string; amenity: string }) => (
    <div key={id}>{amenity}</div>
  ));

  const amenitiesRecs = recommendationsBySection[REVIEW_PAGE_SECTIONS.AMENITIES];

  return (
    <div data-testid="AmenitiesContent" data-group-title="Amenities">
      <Box css={reviewSectionStyles} data-testid="InteriorAmenities">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {!active && !isInteriorRequirementFulfilled && !isDisabled ? (
                <AccordionIconError title="Laundry incomplete error" />
              ) : (
                <>
                  {amenitiesRecs?.[RECS.LAUNDRY] ? (
                    <RecommendationIconBulb
                      fieldName="laundry"
                      removeFlex={true}
                      onClick={() => setDisplayRecsModal(RECS.LAUNDRY)}
                    />
                  ) : amenitiesRecs?.[RECS.AMENITIES] ? (
                    <RecommendationIconBulb
                      fieldName="interior-amenities"
                      removeFlex={true}
                      onClick={() => setDisplayRecsModal(RECS.AMENITIES)}
                    />
                  ) : null}
                </>
              )}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Interior amenities
              </Heading>
              {recommendationTypes.has(RECS.AMENITIES) && (
                <TextButton
                  aria-label="learn-more-about-interior-amenities"
                  textStyle="body"
                  onClick={() => setDisplayRecsModal(RECS.AMENITIES)}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            buttonFieldName="interior-amenities"
            value={SF_MODAL_COMPONENTS.AmenitiesInteriorFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>

        <FieldValueDisplay
          value={displayInteriorAmenities.length ? displayInteriorAmenities : customNoneFieldValueText}
        />
      </Box>

      <Box css={reviewSectionStyles} data-testid="PropertyAmenities">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {amenitiesRecs?.[RECS.AMENITIES] && (
                <RecommendationIconBulb
                  fieldName="property-amenities"
                  removeFlex={true}
                  onClick={() => setDisplayRecsModal(RECS.AMENITIES)}
                />
              )}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Heading level={4} css={{ textStyle: 'body-bold' }}>
                Property amenities
              </Heading>
              {recommendationTypes.has(RECS.AMENITIES) && (
                <TextButton
                  aria-label="learn-more-about-property-amenities"
                  textStyle="body"
                  onClick={() => setDisplayRecsModal(RECS.AMENITIES)}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            buttonFieldName="property-amenities"
            value={SF_MODAL_COMPONENTS.AmenitiesPropertyFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>

        <FieldValueDisplay value={displayPropertyAmenities.length ? displayPropertyAmenities : undefined} />
      </Box>

      <Box data-testid="AdditionalAmenities">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles} />
            <Heading level={4} css={{ textStyle: 'body-bold' }}>
              Additional amenities
            </Heading>
          </Box>

          <ContentSectionEditButton
            buttonFieldName="amenities"
            value={SF_MODAL_COMPONENTS.AmenitiesAdditionalFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>

        <FieldValueDisplay value={displayAdditionalAmenities} />
      </Box>

      {displayRecsModal && (
        <RecommendationModal onClose={() => setDisplayRecsModal('')} recommendationType={displayRecsModal} />
      )}
    </div>
  );
};

export default AmenitiesContent;
